import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { TrashIcon } from '@heroicons/react/24/outline';

const EstimateAddNotes = () => {
  const { id, key } = useParams();
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;

  // This will hold the grouped array: [ { cat: "Service", list: [...notes] }, { cat: "Term...", list: [...] }, ... ]
  const [categories, setCategories] = useState([]);

  // Keep track of which notes are checked: { [noteId]: true/false }
  const [checkedItems, setCheckedItems] = useState({});

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchNotes = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${apiUrl}/best_crud.php`, {
          params: {
            estimate: id,
            builder_id: key,
            getSystemNotes: true,  // Your new grouped endpoint
          },
        });
        console.log('Response for system notes:', response.data);

        if (response.data[0] === 'success') {
          // response.data[1] is the grouped array: [ { cat, list }, ... ]
          setCategories(response.data[1]);
        } else {
          toast.error('No notes found.', { position: 'top-center' });
        }
      } catch (error) {
        console.error(error);
        toast.error('Error fetching notes.', { position: 'top-center' });
      } finally {
        setLoading(false);
      }
    };
    fetchNotes();
  }, [apiUrl, id, key]);

  // Called when a user toggles a checkbox
  const handleCheckboxChange = (noteId) => {
    setCheckedItems((prev) => ({
      ...prev,
      [noteId]: !prev[noteId],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Convert "checkedItems" to the format your backend needs
    // e.g. { 1: true, 2: false, 3: true, ... }

    const bodyData = {
      noteList: checkedItems,
      status: 'addItemNotes',
      estimate: id,
      builder_id: key,
    };

    console.log('Submitting notes:', bodyData);
    
    try {
      const response = await axios.post(`${apiUrl}/best_crud.php`, bodyData, {
        headers: { 'Content-Type': 'application/json' },
      });
      console.log('Response for saving notes:', response.data);
      
      if (response.data[0] === 'success') {
        toast.success('Notes saved!', { position: 'top-center' });
      } else {
        toast.error(response.data.error || 'Failed updating.', { position: 'top-center' });
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        navigate('/login');
      } else if (error.response && error.response.status === 403) {
        navigate('/b_estimate');
      }
    }
  };

  const handleDeleteNotes = async (noteId, updateId) => {
    try {
     const bodyData = {
        status: 'deleteItemNotes',
        estimate: id,
        builder_id: key,
        noteId: noteId,
      }
      console.log('Deleting note:', bodyData);

      const response = await axios.post(`${apiUrl}/best_crud.php`, bodyData, 
        { headers: { 'Content-Type': 'application/json' }
      });
      console.log('Response for deleting note:', response.data);

      if (response.data[0] === 'success') {
        toast.success('Note Deleted', { position: 'top-center' });

        // Update categories so that the matching note has is_selected = false
        setCategories((prevCategories) =>
          prevCategories.map((group) => {
            return {
              ...group,
              list: group.list.map((note) => {
                if (note.id === updateId) {
                  return { ...note, is_selected: false };
                }
                return note;
              })
            };
          })
        );

      } else {
        toast.error(response.data.error || 'Failed to delete note.', { position: 'top-center' });
      }
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error('An error occurred. Please try again.', { position: 'top-center' });
    }
  };


  if (loading) {
    return <p>Loading...</p>;
  }

  return (
    <div className="mx-auto max-w-[800px] px-6 py-8 bg-white">
      <form name="addingForm" onSubmit={handleSubmit} className="space-y-8">
        {categories.map((group, idx) => (
          <div key={idx}>
            {/* Each category gets an <h3> */}
            <h3 className="text-md font-semibold border-b mb-2">
              {group.cat} Notes
            </h3>

            {group.list.map((note) =>
              note.is_selected ? (
                // Render a different layout if already selected
                <div key={note.id} className="text-sm text-gray-500 border-b border-dotted border-gray-300 py-2 flex items-center gap-2">
                  <TrashIcon className='h-5 w-5 min-w-5 text-red-700 hover:cursor-pointer' onClick={() => handleDeleteNotes(note.selected_id, note.id)}/>
                  <p> {note.notes}</p>
                </div>
              ) : (
                // Otherwise, render the checkbox version
                <div key={note.id} className="flex items-center border-b border-dotted border-gray-300 py-2">
                  <input
                    type="checkbox"
                    checked={!!checkedItems[note.id]}
                    onChange={() => handleCheckboxChange(note.id)}
                    className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                  />
                  <p className="ml-4 text-sm text-gray-900">
                    {`${note.key_order}: ${note.notes}`}
                  </p>
                </div>
              )
            )}
          </div>
        ))}

        <div className="mt-6 flex justify-end">
          <button
            type="submit"
            className="text-sm font-semibold leading-6 text-white bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded"
          >
            Submit
          </button>
        </div>
      </form>
    </div>
  );
};

export default EstimateAddNotes;
